import { changeOwnPasswordSchema } from '@gc/shared';

import { ApiError } from '@/lib/api';

/**
 * The change-password form's rules, kept out of the component so they can be
 * pinned without a DOM (this workspace ships none).
 *
 * The pair (current, new) is judged by `changeOwnPasswordSchema` — the very
 * schema the server's `ZodPipe` applies — so the form cannot accept what the
 * server will refuse. Only the "type it twice" check is the form's own.
 */

export interface ChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export type ChangePasswordField = keyof ChangePasswordForm;

/** Field -> i18n key of the message to show under it. */
export type ChangePasswordErrors = Partial<Record<ChangePasswordField, string>>;

export const EMPTY_CHANGE_PASSWORD_FORM: ChangePasswordForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

export function validateChangePassword(form: ChangePasswordForm): ChangePasswordErrors {
  const errors: ChangePasswordErrors = {};
  const parsed = changeOwnPasswordSchema.safeParse({
    currentPassword: form.currentPassword,
    newPassword: form.newPassword,
  });

  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const field = issue.path[0];
      if (field === 'currentPassword' && !errors.currentPassword) {
        errors.currentPassword =
          issue.code === 'too_big'
            ? 'account.password.errors.tooLong'
            : 'account.password.errors.currentRequired';
      }
      if (field === 'newPassword' && !errors.newPassword) {
        errors.newPassword =
          issue.code === 'too_small'
            ? 'account.password.errors.tooShort'
            : issue.code === 'too_big'
              ? 'account.password.errors.tooLong'
              : 'account.password.errors.sameAsCurrent';
      }
    }
  }

  if (form.confirmPassword !== form.newPassword) {
    errors.confirmPassword = 'account.password.errors.mismatch';
  }

  return errors;
}

/**
 * What to tell the person when the server refuses. A wrong current password
 * lands on that field; everything else is a line above the button.
 *
 * An answer that never arrived is not a refusal: the server may well have
 * saved the new password before the connection dropped, so "could not change
 * it" would be a guess — and a wrong guess leaves someone signed out, not
 * knowing which password works. That case says so, and which one to try.
 */
export function describeChangePasswordFailure(error: unknown): {
  field?: ChangePasswordField;
  key: string;
} {
  if (!(error instanceof ApiError) || error.status === 502 || error.status === 504) {
    return { key: 'account.password.errors.unconfirmed' };
  }
  if (error.code === 'wrong_current_password') {
    return { field: 'currentPassword', key: 'account.password.errors.wrongCurrent' };
  }
  if (error.status === 429) return { key: 'account.password.errors.tooMany' };
  if (error.status === 401) return { key: 'auth.sessionExpired' };
  if (error.code === 'validation_failed') return { key: 'account.password.errors.invalid' };
  return { key: 'account.password.errors.failed' };
}
